/**
 * safeSchema.ts — Null-safe wrappers for ALL nullable schema builders
 * ═══════════════════════════════════════════════════════════════════════════
 * PURPOSE
 * ───────
 * Several schema builder functions return `null` under certain conditions:
 *
 *   buildFAQPageSchema        → null when faqs is empty / undefined
 *   buildAggregateRatingSchema → null when reviewCount === 0
 *   buildInventoryListSchema  → null when vehicles array is empty
 *   buildAggregateOfferSchema → null when no available vehicles exist
 *   buildVideoListSchema      → null when videos array is empty
 *
 * Callers that forget to null-check before mutating the result (e.g.
 * `schema['@id'] = '...'`) will throw a TypeError at runtime.
 *
 * This module provides a single safe entry-point for every nullable builder:
 *   1. Calls the underlying builder
 *   2. Bails out immediately if the result is null — no throw, no DOM write
 *   3. Optionally overrides the `@id` field
 *   4. Calls `injectJsonLd` — all in one call, zero null-check boilerplate
 *
 * USAGE
 * ─────
 *   // FAQ
 *   injectFAQSchema('schema-financing-faq', pageFaqs.financing, 'https://rockhoundauto.com/financing#faqpage');
 *
 *   // AggregateRating
 *   injectAggregateRatingSchema('schema-reviews-rating', { entityType: 'LocalBusiness', ... });
 *
 *   // Inventory list
 *   injectInventoryListSchema('schema-inventory-list', vehicles);
 *
 *   // AggregateOffer
 *   injectAggregateOfferSchema('schema-inventory-offer', vehicles);
 *
 *   // Video list
 *   injectVideoListSchema('schema-videos-list', videos);
 *
 * IMPORT
 * ──────
 *   import { injectFAQSchema, removeFAQSchema, injectAggregateRatingSchema,
 *            injectInventoryListSchema, injectAggregateOfferSchema,
 *            injectVideoListSchema } from '../../lib/safeSchema';
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import {
  buildFAQPageSchema,
  buildAggregateRatingSchema,
  buildInventoryListSchema,
  buildAggregateOfferSchema,
  buildVideoListSchema,
  injectJsonLd,
  removeJsonLd,
} from './schemaBuilder';

// ─── Type aliases (mirrors the shapes expected by each builder) ───────────────

/** Shape accepted by `buildFAQPageSchema` */
type FAQItem = { question: string; answer: string };

/** Shape accepted by `buildAggregateRatingSchema` */
type AggregateRatingConfig = Parameters<typeof buildAggregateRatingSchema>[0];

/** Shape accepted by `buildInventoryListSchema` */
type InventoryVehicle = Parameters<typeof buildInventoryListSchema>[0][number];

/** Shape accepted by `buildAggregateOfferSchema` */
type OfferVehicle = Parameters<typeof buildAggregateOfferSchema>[0][number];

/** Shape accepted by `buildVideoListSchema` */
type VideoItem = Parameters<typeof buildVideoListSchema>[0][number];

// ─── FAQ ──────────────────────────────────────────────────────────────────────

/**
 * Safely build and inject a FAQPage JSON-LD schema.
 *
 * - Does nothing (no throw, no DOM write) when `faqs` is null, undefined, or empty.
 * - Optionally overrides the `@id` field with a page-specific URL fragment.
 *
 * @param schemaId  - DOM element id for the `<script>` tag (e.g. `'schema-financing-faq'`)
 * @param faqs      - FAQ array from mock data or a real data source
 * @param pageId    - Optional full URL to use as the schema `@id`
 *                    (e.g. `'https://rockhoundauto.com/financing#faqpage'`)
 */
export function injectFAQSchema(
  schemaId: string,
  faqs: FAQItem[] | undefined | null,
  pageId?: string
): void {
  const schema = buildFAQPageSchema(faqs);
  if (!schema) return; // empty / null — nothing to inject

  if (pageId) {
    (schema as Record<string, unknown>)['@id'] = pageId;
  }

  injectJsonLd(schemaId, schema as Record<string, unknown>);
}

/**
 * Remove a previously injected FAQPage schema.
 * Thin alias over `removeJsonLd` — kept here so callers only need one import.
 *
 * @param schemaId - Same id that was passed to `injectFAQSchema`
 */
export function removeFAQSchema(schemaId: string): void {
  removeJsonLd(schemaId);
}

// ─── AggregateRating ──────────────────────────────────────────────────────────

/**
 * Safely build and inject an AggregateRating JSON-LD schema.
 *
 * - Does nothing when `config.reviewCount === 0` (builder returns null).
 * - Prevents the common crash pattern:
 *     const s = buildAggregateRatingSchema(cfg);
 *     s['@id'] = '...';  // ← TypeError when reviewCount is 0
 *
 * @param schemaId - DOM element id for the `<script>` tag
 * @param config   - Rating configuration (entityType, name, url, ratingValue, reviewCount, …)
 */
export function injectAggregateRatingSchema(
  schemaId: string,
  config: AggregateRatingConfig
): void {
  const schema = buildAggregateRatingSchema(config);
  if (!schema) return; // reviewCount === 0 — nothing to inject

  injectJsonLd(schemaId, schema);
}

/**
 * Remove a previously injected AggregateRating schema.
 */
export function removeAggregateRatingSchema(schemaId: string): void {
  removeJsonLd(schemaId);
}

// ─── InventoryList ────────────────────────────────────────────────────────────

/**
 * Safely build and inject an ItemList (inventory) JSON-LD schema.
 *
 * - Does nothing when `vehicles` is empty (builder returns null).
 * - Prevents the common crash pattern:
 *     const s = buildInventoryListSchema([]);
 *     s['numberOfItems'] = 0;  // ← TypeError when vehicles is empty
 *
 * @param schemaId - DOM element id for the `<script>` tag
 * @param vehicles - Array of vehicle objects
 */
export function injectInventoryListSchema(
  schemaId: string,
  vehicles: InventoryVehicle[]
): void {
  const schema = buildInventoryListSchema(vehicles);
  if (!schema) return; // empty array — nothing to inject

  injectJsonLd(schemaId, schema);
}

/**
 * Remove a previously injected InventoryList schema.
 */
export function removeInventoryListSchema(schemaId: string): void {
  removeJsonLd(schemaId);
}

// ─── AggregateOffer ───────────────────────────────────────────────────────────

/**
 * Safely build and inject an AggregateOffer JSON-LD schema.
 *
 * - Does nothing when no vehicles have `status === 'available'` (builder returns null).
 * - Prevents the common crash pattern:
 *     const s = buildAggregateOfferSchema(vehicles);
 *     s['offerCount'] = 0;  // ← TypeError when no available vehicles
 *
 * @param schemaId - DOM element id for the `<script>` tag
 * @param vehicles - Array of vehicle objects with `price` and `status`
 */
export function injectAggregateOfferSchema(
  schemaId: string,
  vehicles: OfferVehicle[]
): void {
  const schema = buildAggregateOfferSchema(vehicles);
  if (!schema) return; // no available vehicles — nothing to inject

  injectJsonLd(schemaId, schema);
}

/**
 * Remove a previously injected AggregateOffer schema.
 */
export function removeAggregateOfferSchema(schemaId: string): void {
  removeJsonLd(schemaId);
}

// ─── VideoList ────────────────────────────────────────────────────────────────

/**
 * Safely build and inject a VideoObject ItemList JSON-LD schema.
 *
 * - Does nothing when `videos` is empty (builder returns null).
 * - Prevents the common crash pattern:
 *     const s = buildVideoListSchema([]);
 *     s['numberOfItems'] = 0;  // ← TypeError when videos is empty
 *
 * @param schemaId - DOM element id for the `<script>` tag
 * @param videos   - Array of video objects
 */
export function injectVideoListSchema(
  schemaId: string,
  videos: VideoItem[]
): void {
  const schema = buildVideoListSchema(videos);
  if (!schema) return; // empty array — nothing to inject

  injectJsonLd(schemaId, schema);
}

/**
 * Remove a previously injected VideoList schema.
 */
export function removeVideoListSchema(schemaId: string): void {
  removeJsonLd(schemaId);
}
